import React, { useEffect, useMemo, useState } from "react";
import {
  addInwardEntry,
  addInwardPayment,
  addSalesEntry,
  addSalesPayment,
  getCustomers,
  getInwardEntries,
  getSalesEntries,
  getSuppliers,
  updateInwardEntry,
  updateSalesEntry,
  updateSalesEntryPaymentStatus,
} from "../storage";
import { formatDisplayDate, toComparableYmd } from "../utils/dateFormat";
import { getLogoDataUrl } from "../utils/receiptPrint";
import {
  buildLedgerPrintHtml,
  buildLedgerStatement,
  buildPartyLedgerSummaries,
  filterMainLedgerEntries,
  formatLedgerMoney,
  isLedgerOpeningBalanceEntry,
  LEDGER_OPENING_BALANCE_CATEGORY,
  LEDGER_OPENING_BALANCE_DATE,
  LEDGER_PAYMENT_MODES,
  LEDGER_WINDOW_DAYS,
  ledgerPartyName,
  localLedgerToday,
} from "./paymentLedgerModel";
import "./PaymentLedger.css";

function sumSummaries(rows) {
  return rows.reduce(
    (totals, row) => {
      totals.totalAmount += Number(row.totalAmount) || 0;
      totals.paidAmount += Number(row.paidAmount) || 0;
      totals.adjustmentAmount += Number(row.adjustmentAmount) || 0;
      totals.pendingAmount += Number(row.pendingAmount) || 0;
      totals.entryCount += Number(row.entryCount) || 0;
      return totals;
    },
    { totalAmount: 0, paidAmount: 0, adjustmentAmount: 0, pendingAmount: 0, entryCount: 0 }
  );
}

function makePaymentForm() {
  return { date: localLedgerToday(), amount: "", mode: "Cash" };
}

function entryDate(type, entry) {
  return toComparableYmd(type === "purchase" ? entry?.createdAt : entry?.saleDate);
}

async function printStatement(statement) {
  const printWindow = window.open("", "PAYMENT_LEDGER_PRINT", "height=850,width=1100");
  if (!printWindow) {
    alert("Please allow popups to print the payment ledger.");
    return;
  }

  const logoUrl = await getLogoDataUrl().catch(() => "");
  printWindow.document.write(buildLedgerPrintHtml(statement, logoUrl));
  printWindow.document.close();

  const images = Array.from(printWindow.document.images || []);
  const imageReady = Promise.all(
    images.map(
      (image) =>
        new Promise((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }
          image.addEventListener("load", resolve, { once: true });
          image.addEventListener("error", resolve, { once: true });
        })
    )
  );
  await Promise.race([imageReady, new Promise((resolve) => setTimeout(resolve, 2000))]);
  printWindow.focus();
  printWindow.print();
}

function LedgerOverview({ title, tone, totals, paidLabel }) {
  return (
    <article className={`ledger-overview ledger-overview-${tone}`}>
      <div className="ledger-overview-heading">
        <span>{title}</span>
        <strong>{totals.entryCount} entries</strong>
      </div>
      <div className="ledger-overview-total">
        <small>Total Amount</small>
        <strong>Rs. {formatLedgerMoney(totals.totalAmount)}</strong>
      </div>
      <div className="ledger-overview-grid">
        <div><span>{paidLabel}</span><strong>Rs. {formatLedgerMoney(totals.paidAmount)}</strong></div>
        <div><span>Pending</span><strong>Rs. {formatLedgerMoney(totals.pendingAmount)}</strong></div>
      </div>
    </article>
  );
}

export default function PaymentLedger() {
  const [inwardEntries, setInwardEntries] = useState([]);
  const [salesEntries, setSalesEntries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [activeType, setActiveType] = useState("purchase");
  const [partyFilters, setPartyFilters] = useState({ purchase: "", sales: "" });
  const [asOfDate, setAsOfDate] = useState(localLedgerToday);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentForm, setPaymentForm] = useState(makePaymentForm);
  const [openingBalanceOpen, setOpeningBalanceOpen] = useState(false);
  const [openingBalanceSaving, setOpeningBalanceSaving] = useState(false);
  const [openingBalanceAmount, setOpeningBalanceAmount] = useState("");

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const [purchases, sales, supplierList, customerList] = await Promise.all([
        getInwardEntries(),
        getSalesEntries(),
        getSuppliers(),
        getCustomers(),
      ]);
      setInwardEntries(Array.isArray(purchases) ? purchases : []);
      setSalesEntries(Array.isArray(sales) ? sales : []);
      setSuppliers(Array.isArray(supplierList) ? supplierList : []);
      setCustomers(Array.isArray(customerList) ? customerList : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to load payment ledger data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const purchaseSummaries = useMemo(
    () => buildPartyLedgerSummaries("purchase", inwardEntries, asOfDate, suppliers),
    [inwardEntries, asOfDate, suppliers]
  );
  const salesSummaries = useMemo(
    () => buildPartyLedgerSummaries("sales", salesEntries, asOfDate, customers),
    [salesEntries, asOfDate, customers]
  );
  const purchaseTotals = useMemo(() => sumSummaries(purchaseSummaries), [purchaseSummaries]);
  const salesTotals = useMemo(() => sumSummaries(salesSummaries), [salesSummaries]);

  const activeEntries = activeType === "purchase" ? inwardEntries : salesEntries;
  const activeSummaries = activeType === "purchase" ? purchaseSummaries : salesSummaries;
  const activeParty = partyFilters[activeType];
  const activePartyKey = activeParty.toLocaleLowerCase();
  const activeOpeningEntry = activeEntries.find(
    (entry) =>
      isLedgerOpeningBalanceEntry(entry) &&
      ledgerPartyName(activeType, entry).toLocaleLowerCase() === activePartyKey
  );
  const activeOpeningSettledAmount = (Array.isArray(activeOpeningEntry?.payments)
    ? activeOpeningEntry.payments
    : []
  ).reduce((total, payment) => total + (Number(payment?.amount) || 0), 0);
  const activeStatement = useMemo(
    () =>
      activeParty
        ? buildLedgerStatement({ type: activeType, entries: activeEntries, partyName: activeParty, asOfDate })
        : null,
    [activeType, activeEntries, activeParty, asOfDate]
  );
  const currentPaymentStatement = useMemo(
    () =>
      activeParty
        ? buildLedgerStatement({
            type: activeType,
            entries: activeEntries,
            partyName: activeParty,
            asOfDate: localLedgerToday(),
          })
        : null,
    [activeType, activeEntries, activeParty]
  );

  const setParty = (value) => {
    setPartyFilters((current) => ({ ...current, [activeType]: value }));
    setPaymentOpen(false);
    setOpeningBalanceOpen(false);
  };

  const switchLedger = (type) => {
    setActiveType(type);
    setPaymentOpen(false);
    setOpeningBalanceOpen(false);
  };

  const openPayment = () => {
    if (!activeParty || !currentPaymentStatement || currentPaymentStatement.pendingAmount <= 0) return;
    setAsOfDate(localLedgerToday());
    setPaymentForm(makePaymentForm());
    setPaymentOpen(true);
  };

  const closePayment = () => {
    if (paymentSaving) return;
    setPaymentOpen(false);
    setPaymentForm(makePaymentForm());
  };

  const openOpeningBalance = () => {
    if (!activeParty) return;
    setOpeningBalanceAmount(
      activeOpeningEntry ? String(Number(activeOpeningEntry.total) || 0) : ""
    );
    setOpeningBalanceOpen(true);
  };

  const closeOpeningBalance = () => {
    if (openingBalanceSaving) return;
    setOpeningBalanceOpen(false);
    setOpeningBalanceAmount("");
  };

  const saveOpeningBalance = async (event) => {
    event.preventDefault();
    if (openingBalanceSaving || !activeParty) return;

    const amount = +Number(openingBalanceAmount || 0).toFixed(2);
    const settledAmount = +Number(activeOpeningSettledAmount || 0).toFixed(2);
    if (!Number.isFinite(amount) || amount < 0 || (!activeOpeningEntry && amount <= 0)) {
      alert("Enter a valid opening balance amount.");
      return;
    }
    if (amount + 0.0001 < settledAmount) {
      alert(`Opening balance cannot be less than the already settled amount of Rs. ${formatLedgerMoney(settledAmount)}.`);
      return;
    }

    setOpeningBalanceSaving(true);
    try {
      if (activeType === "purchase") {
        const payload = {
          supplierName: activeParty,
          category: LEDGER_OPENING_BALANCE_CATEGORY,
          numberOfBoxes: 0,
          createdAt: `${LEDGER_OPENING_BALANCE_DATE}T00:00:00.000Z`,
          total: amount,
          totalQty: 1,
          items: [{
            date: LEDGER_OPENING_BALANCE_DATE,
            material: LEDGER_OPENING_BALANCE_CATEGORY,
            qty: 1,
            rate: amount,
            amount,
            stockQty: 0,
            closingQty: 0,
            closingRate: 0,
            closingAmount: 0,
            retailQty: 0,
            retailRate: 0,
            retailAmount: 0,
          }],
        };
        if (activeOpeningEntry?.id) await updateInwardEntry(activeOpeningEntry.id, payload);
        else await addInwardEntry(payload);
      } else {
        const master = customers.find(
          (customer) => String(customer?.name || "").trim().toLocaleLowerCase() === activePartyKey
        );
        const snapshot = activeEntries.find(
          (entry) => ledgerPartyName("sales", entry).toLocaleLowerCase() === activePartyKey
        );
        const customerPhone = String(master?.phone || snapshot?.customerPhone || "").trim();
        const customerAddress = String(master?.address || snapshot?.customerAddress || "").trim();
        const customerAddress2 = String(master?.address2 || snapshot?.customerAddress2 || "").trim();
        const payload = {
          category: LEDGER_OPENING_BALANCE_CATEGORY,
          saleDate: LEDGER_OPENING_BALANCE_DATE,
          customer: [activeParty, customerPhone, customerAddress, customerAddress2].filter(Boolean).join("\n"),
          customerName: activeParty,
          customerPhone,
          customerAddress,
          customerAddress2,
          source: LEDGER_OPENING_BALANCE_CATEGORY,
          transporterName: "",
          numberOfBoxes: 0,
          paymentDone: false,
          paymentType: "Credit",
          discountAmount: 0,
          items: [{
            material: LEDGER_OPENING_BALANCE_CATEGORY,
            qty: 1,
            rate: amount,
            amount,
            boxNumber: "",
            closingStock: 0,
          }],
        };
        if (activeOpeningEntry?.id) await updateSalesEntry(activeOpeningEntry.id, payload);
        else await addSalesEntry(payload);
      }

      await refresh();
      setAsOfDate(localLedgerToday());
      setOpeningBalanceOpen(false);
      setOpeningBalanceAmount("");
      alert(`Opening balance ${activeOpeningEntry ? "updated" : "saved"} successfully.`);
    } catch (err) {
      console.error(err);
      alert(err?.message || "Unable to save opening balance.");
    } finally {
      setOpeningBalanceSaving(false);
    }
  };

  const savePayment = async (event) => {
    event.preventDefault();
    if (paymentSaving || !activeParty) return;

    const amount = +Number(paymentForm.amount || 0).toFixed(2);
    const date = toComparableYmd(paymentForm.date);
    const today = localLedgerToday();
    const mode = LEDGER_PAYMENT_MODES.includes(paymentForm.mode) ? paymentForm.mode : "Cash";
    const available = Number(currentPaymentStatement?.pendingAmount) || 0;
    if (!date || date > today) {
      alert("Select a valid payment date.");
      return;
    }
    if (amount <= 0) {
      alert("Enter a valid payment amount.");
      return;
    }
    if (amount > available + 0.0001) {
      alert(`Payment cannot exceed the pending amount of Rs. ${formatLedgerMoney(available)}.`);
      return;
    }

    const partyKey = activeParty.toLocaleLowerCase();
    const pendingEntries = filterMainLedgerEntries(activeType, activeEntries)
      .filter((entry) => ledgerPartyName(activeType, entry).toLocaleLowerCase() === partyKey)
      .map((entry) => ({
        entry,
        pendingAmount: buildLedgerStatement({
          type: activeType,
          entries: [entry],
          partyName: activeParty,
          asOfDate: today,
        }).pendingAmount,
      }))
      .filter((row) => row.pendingAmount > 0)
      .sort((a, b) => {
        const dateCompare = entryDate(activeType, a.entry).localeCompare(entryDate(activeType, b.entry));
        return dateCompare || String(a.entry?.id || "").localeCompare(String(b.entry?.id || ""));
      });

    let remaining = amount;
    let appliedCount = 0;
    setPaymentSaving(true);
    try {
      for (const row of pendingEntries) {
        if (remaining <= 0) break;
        const applied = +Math.min(remaining, row.pendingAmount).toFixed(2);
        if (applied <= 0) continue;
        const payload = { amount: applied, date, mode, type: "payment" };
        if (activeType === "purchase") await addInwardPayment(row.entry.id, payload);
        else {
          await addSalesPayment(row.entry.id, payload);
          if (!isLedgerOpeningBalanceEntry(row.entry) && applied >= row.pendingAmount - 0.0001) {
            await updateSalesEntryPaymentStatus(row.entry.id, true);
          }
        }
        remaining = +(remaining - applied).toFixed(2);
        appliedCount += 1;
      }
      if (remaining > 0.0001) throw new Error("Unable to allocate the full payment amount.");
      await refresh();
      setAsOfDate(today);
      setPaymentOpen(false);
      setPaymentForm(makePaymentForm());
      alert(`Payment saved successfully across ${appliedCount} ${appliedCount === 1 ? "entry" : "entries"}.`);
    } catch (err) {
      console.error(err);
      await refresh();
      alert(err?.message || "Unable to save payment.");
    } finally {
      setPaymentSaving(false);
    }
  };

  return (
    <div className="payment-ledger-page">
      <header className="payment-ledger-header">
        <div>
          <span className="payment-ledger-kicker">ARF SEAFOODS · ACCOUNTS</span>
          <h1>Payment Ledger</h1>
          <p>Supplier and customer balances with item-wise activity and payment history.</p>
        </div>
        <button type="button" className="ledger-refresh" onClick={refresh} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh Data"}
        </button>
      </header>

      {error ? <div className="ledger-error" role="alert">{error}</div> : null}

      <section className="ledger-overview-pair" aria-label="Purchase and sales ledger totals">
        <LedgerOverview title="Purchase Ledger" tone="purchase" totals={purchaseTotals} paidLabel="Amount Paid" />
        <LedgerOverview title="Sales Ledger" tone="sales" totals={salesTotals} paidLabel="Amount Received" />
      </section>

      <section className={`ledger-workspace ledger-workspace-${activeType}`}>
        <div className="ledger-tabs" role="tablist" aria-label="Payment ledger type">
          <button
            type="button"
            role="tab"
            aria-selected={activeType === "purchase"}
            className={activeType === "purchase" ? "active" : ""}
            onClick={() => switchLedger("purchase")}
          >
            Purchase Payments
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeType === "sales"}
            className={activeType === "sales" ? "active" : ""}
            onClick={() => switchLedger("sales")}
          >
            Sales Payments
          </button>
        </div>

        <div className="ledger-controls">
          <label className="ledger-field ledger-party-field">
            <span>{activeType === "purchase" ? "Supplier" : "Customer"}</span>
            <select value={activeParty} onChange={(event) => setParty(event.target.value)}>
              <option value="">All {activeType === "purchase" ? "suppliers" : "customers"}</option>
              {activeSummaries
                .slice()
                .sort((a, b) => a.partyName.localeCompare(b.partyName, undefined, { sensitivity: "base" }))
                .map((summary) => (
                  <option key={summary.partyName.toLocaleLowerCase()} value={summary.partyName}>
                    {summary.partyName} · Pending Rs. {formatLedgerMoney(summary.pendingAmount)}
                  </option>
                ))}
            </select>
          </label>
          <label className="ledger-field">
            <span>Statement As On</span>
            <input
              type="date"
              value={asOfDate}
              max={localLedgerToday()}
              onChange={(event) => setAsOfDate(event.target.value || localLedgerToday())}
            />
          </label>
          <div className="ledger-control-actions">
            <button
              type="button"
              className="ledger-opening-button"
              disabled={!activeParty}
              onClick={openOpeningBalance}
            >
              {activeOpeningEntry ? "Edit Opening Balance" : "+ Opening Balance"}
            </button>
            <button
              type="button"
              className="ledger-payment-button"
              disabled={!activeParty || !currentPaymentStatement || currentPaymentStatement.pendingAmount <= 0}
              onClick={openPayment}
            >
              + Payment Entry
            </button>
            <button
              type="button"
              className="ledger-print-button"
              disabled={!activeStatement}
              onClick={() => activeStatement && printStatement(activeStatement)}
            >
              Print Statement
            </button>
          </div>
        </div>

        {!activeParty ? (
          <div className="ledger-party-list">
            <div className="ledger-section-heading">
              <div>
                <span>{activeType === "purchase" ? "SUPPLIER-WISE" : "CUSTOMER-WISE"}</span>
                <h2>{activeType === "purchase" ? "Purchase balances" : "Sales balances"}</h2>
              </div>
              <small>Select a party to view and print the latest seven transaction dates.</small>
            </div>
            <div className="ledger-table-wrap">
              <table className="ledger-party-table">
                <thead><tr><th>{activeType === "purchase" ? "Supplier" : "Customer"}</th><th className="number">Entries</th><th className="number">Total Amount</th><th className="number">{activeType === "purchase" ? "Paid" : "Received"}</th><th className="number">Pending</th><th /></tr></thead>
                <tbody>
                  {activeSummaries.length ? activeSummaries.map((summary) => (
                    <tr key={summary.partyName.toLocaleLowerCase()}>
                      <td><strong>{summary.partyName}</strong></td>
                      <td className="number">{summary.entryCount}</td>
                      <td className="number">{formatLedgerMoney(summary.totalAmount)}</td>
                      <td className="number ledger-paid-text">{formatLedgerMoney(summary.paidAmount)}</td>
                      <td className="number ledger-pending-text">{formatLedgerMoney(summary.pendingAmount)}</td>
                      <td><button type="button" className="ledger-view-button" onClick={() => setParty(summary.partyName)}>View Ledger</button></td>
                    </tr>
                  )) : (
                    <tr><td colSpan={6} className="ledger-empty">No {activeType} ledger records found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="ledger-statement-panel">
            <div className="ledger-statement-heading">
              <div>
                <span>{activeStatement.partyLabel.toUpperCase()}</span>
                <h2>{activeStatement.partyName}</h2>
                <p>Latest {LEDGER_WINDOW_DAYS} transaction dates · {formatDisplayDate(activeStatement.windowStart)} to {formatDisplayDate(activeStatement.windowEnd)}</p>
              </div>
              <button type="button" onClick={() => setParty("")}>View all parties</button>
            </div>

            <div className="ledger-statement-summary">
              <div><span>{activeStatement.amountLabel}</span><strong>Rs. {formatLedgerMoney(activeStatement.totalAmount)}</strong></div>
              <div><span>{activeStatement.paidLabel}</span><strong>Rs. {formatLedgerMoney(activeStatement.paidAmount)}</strong></div>
              <div><span>Adjustments</span><strong>Rs. {formatLedgerMoney(activeStatement.adjustmentAmount)}</strong></div>
              <div className="pending"><span>Amount Pending</span><strong>Rs. {formatLedgerMoney(activeStatement.pendingAmount)}</strong></div>
            </div>

            <div className="ledger-table-wrap">
              <table className="ledger-detail-table">
                <thead><tr><th>Date</th><th>Particulars</th><th className="number">Qty</th><th className="number">Rate</th><th className="number">Amount</th><th className="number">{activeType === "purchase" ? "Paid" : "Received"}</th><th>Payment Mode</th><th className="number">Balance</th></tr></thead>
                <tbody>
                  <tr className="ledger-opening-row">
                    <td>{formatDisplayDate(activeStatement.windowStart)}</td>
                    <td colSpan={6}>Balance brought forward</td>
                    <td className="number">{formatLedgerMoney(activeStatement.openingBalance)}</td>
                  </tr>
                  {activeStatement.rows.length ? activeStatement.rows.map((row, index) => (
                    <tr key={`${row.date}-${row.kind}-${row.particulars}-${row.mode}-${index}`} className={`ledger-row-${row.kind}`}>
                      <td>{formatDisplayDate(row.date)}</td>
                      <td><strong>{row.particulars}</strong>{row.references.length ? <small>{row.references.length} reference{row.references.length === 1 ? "" : "s"}</small> : null}</td>
                      <td className="number">{row.kind === "item" ? Number(row.qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 }) : ""}</td>
                      <td className="number">{row.kind === "item" && row.rate ? formatLedgerMoney(row.rate) : ""}</td>
                      <td className="number">{row.debit ? formatLedgerMoney(row.debit) : ""}</td>
                      <td className="number ledger-paid-text">{row.credit ? formatLedgerMoney(row.credit) : ""}</td>
                      <td>{row.mode || ""}</td>
                      <td className="number ledger-balance-text">{formatLedgerMoney(row.runningBalance)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={8} className="ledger-empty">No transactions up to the selected date.</td></tr>
                  )}
                  <tr className="ledger-closing-row"><td colSpan={7}>Closing Balance</td><td className="number">{formatLedgerMoney(activeStatement.pendingAmount)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {paymentOpen ? (
        <div className="ledger-payment-backdrop" onMouseDown={closePayment} role="presentation">
          <section className="ledger-payment-dialog" role="dialog" aria-modal="true" aria-labelledby="ledger-payment-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="ledger-payment-heading">
              <div><span>PAYMENT ENTRY</span><h2 id="ledger-payment-title">{activeType === "purchase" ? "Pay Supplier" : "Receive from Customer"}</h2><p>{activeParty}</p></div>
              <button type="button" onClick={closePayment} disabled={paymentSaving}>Close</button>
            </div>
            <form className="ledger-payment-form" onSubmit={savePayment}>
              <div className="ledger-payment-pending"><span>Current Pending</span><strong>Rs. {formatLedgerMoney(currentPaymentStatement?.pendingAmount || 0)}</strong></div>
              <label><span>Paid Date</span><input type="date" max={localLedgerToday()} value={paymentForm.date} onChange={(event) => setPaymentForm((current) => ({ ...current, date: event.target.value }))} required /></label>
              <label><span>Payment Mode</span><select value={paymentForm.mode} onChange={(event) => setPaymentForm((current) => ({ ...current, mode: event.target.value }))}>{LEDGER_PAYMENT_MODES.map((mode) => <option key={mode} value={mode}>{mode}</option>)}</select></label>
              <label className="wide"><span>Amount</span><input type="number" min="0.01" step="0.01" max={currentPaymentStatement?.pendingAmount || undefined} value={paymentForm.amount} onChange={(event) => setPaymentForm((current) => ({ ...current, amount: event.target.value }))} placeholder="Enter payment amount" required /></label>
              <div className="ledger-payment-actions"><button type="button" className="ledger-cancel-button" onClick={closePayment} disabled={paymentSaving}>Cancel</button><button type="submit" className="ledger-payment-button" disabled={paymentSaving}>{paymentSaving ? "Saving..." : "Save Payment"}</button></div>
            </form>
          </section>
        </div>
      ) : null}

      {openingBalanceOpen ? (
        <div className="ledger-payment-backdrop" onMouseDown={closeOpeningBalance} role="presentation">
          <section className="ledger-payment-dialog" role="dialog" aria-modal="true" aria-labelledby="ledger-opening-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="ledger-payment-heading ledger-opening-heading">
              <div>
                <span>OPENING BALANCE</span>
                <h2 id="ledger-opening-title">
                  {activeOpeningEntry ? "Update" : "Add"} {activeType === "purchase" ? "Supplier" : "Customer"} Opening Balance
                </h2>
                <p>{activeParty}</p>
              </div>
              <button type="button" onClick={closeOpeningBalance} disabled={openingBalanceSaving}>Close</button>
            </div>
            <form className="ledger-payment-form" onSubmit={saveOpeningBalance}>
              <div className="ledger-payment-pending ledger-opening-summary">
                <span>Already Settled Against Opening Balance</span>
                <strong>Rs. {formatLedgerMoney(activeOpeningSettledAmount)}</strong>
              </div>
              <p className="ledger-opening-help">
                Enter the total pending amount that existed before transactions were recorded in this application.
                Future payment entries will reduce this balance automatically.
              </p>
              <label className="wide">
                <span>Already Pending Balance</span>
                <input
                  type="number"
                  min={activeOpeningSettledAmount || (activeOpeningEntry ? 0 : 0.01)}
                  step="0.01"
                  value={openingBalanceAmount}
                  onChange={(event) => setOpeningBalanceAmount(event.target.value)}
                  placeholder="Enter opening balance amount"
                  autoFocus
                  required
                />
              </label>
              <div className="ledger-payment-actions">
                <button type="button" className="ledger-cancel-button" onClick={closeOpeningBalance} disabled={openingBalanceSaving}>Cancel</button>
                <button type="submit" className="ledger-opening-button" disabled={openingBalanceSaving}>
                  {openingBalanceSaving ? "Saving..." : activeOpeningEntry ? "Update Opening Balance" : "Save Opening Balance"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}
